/**
 * ReplicationRouter - Routes IPC replication messages to coordinators
 *
 * This is a simple routing layer that delivers IPC state replication messages
 * from Electron to the appropriate coordinator handlers.
 *
 * Key responsibilities:
 * - Register handlers for specific IPC channels
 * - Route incoming replication messages to all registered handlers
 * - Return true if message was handled by coordinator, false for legacy fallback
 *
 * Pattern (follows MessageRouter.ts):
 * IPC Replication → ReplicationRouter → Coordinator Handler → State Update → Event Emission
 *
 * Usage:
 * ```typescript
 * // In coordinator initialization:
 * gameLaunchCoordinator.registerReplicationHandler(replicationRouter);
 *
 * // In coordinator:
 * registerReplicationHandler(router: IReplicationRouter): void {
 *   router.register('playjectorHostModeState', this.onReplicationReceived.bind(this));
 * }
 *
 * // In IPC handler:
 * const routed = replicationRouter.route(channel, state);
 * if (!routed) {
 *   // Fall back to legacy store.updateState()
 * }
 * ```
 */

// ========================================
// ReplicationRouter Interface
// ========================================

/**
 * Handler function type for replication messages
 * Uses unknown for state to force type narrowing in handlers
 */
export type ReplicationHandler = (state: unknown) => void;

/**
 * IReplicationRouter - Interface for IPC replication routing
 *
 * Accepts replication messages with a channel name and routes them to registered handlers.
 */
export interface IReplicationRouter {
  /**
   * Register a handler for a specific IPC channel
   *
   * @param channel - The IPC channel name (e.g., 'playjectorHostModeState')
   * @param handler - The function to call when this channel receives replication
   */
  register(
    channel: string,
    handler: ReplicationHandler,
  ): void;

  /**
   * Route an incoming replication message to registered handlers for its channel
   *
   * @param channel - The IPC channel name
   * @param state - The replicated state
   * @returns true if routed to at least one coordinator, false if no handlers
   */
  route(channel: string, state: unknown): boolean;
}

// ========================================
// ReplicationRouter Implementation
// ========================================

/**
 * ReplicationRouter - Simple implementation of IReplicationRouter
 *
 * Routes IPC replication messages based on channel name to registered handlers.
 * Handlers receive the state data and can update their coordinators accordingly.
 */
export class ReplicationRouter implements IReplicationRouter {
  private handlers: Map<string, ReplicationHandler[]> = new Map();

  /**
   * Register a handler for a specific IPC channel
   */
  register(
    channel: string,
    handler: ReplicationHandler,
  ): void {
    if (!this.handlers.has(channel)) {
      this.handlers.set(channel, []);
    }
    const channelHandlers = this.handlers.get(channel);
    if (channelHandlers) {
      channelHandlers.push(handler);
    }
    console.log(`[ReplicationRouter] Registered handler for channel: ${channel}`);
  }

  /**
   * Route an incoming replication message to all registered handlers for its channel
   *
   * @returns true if at least one handler was called, false if no handlers registered
   */
  route(channel: string, state: unknown): boolean {
    const handlers = this.handlers.get(channel);
    if (!handlers || handlers.length === 0) {
      // No handlers registered - caller should fall back to legacy
      return false;
    }

    handlers.forEach((handler) => {
      try {
        handler(state);
      } catch (error) {
        console.error(`[ReplicationRouter] Handler error for channel ${channel}:`, error);
      }
    });

    return true; // At least one handler was called
  }

  /**
   * Get list of channels with registered handlers (for testing/debugging)
   */
  getManagedChannels(): string[] {
    return Array.from(this.handlers.keys());
  }
}

// ========================================
// Global Singleton Instance
// ========================================

/**
 * replicationRouter - Singleton for routing IPC replication messages
 *
 * Use this to register handlers in coordinators that need to respond to
 * IPC state replication from Electron.
 */
export const replicationRouter = new ReplicationRouter();
